import React, { useState, useEffect } from 'react';
import { buildDefaultUnitQcReport, OLD_MOBILE_GRADE_OPTIONS, IMEI_QC_KEYS } from '../constants/oldMobileQc';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { addBranchToBody } from '../utils/branchFilter';
import OptionalPhotoUpload from './common/OptionalPhotoUpload';
import { uploadSalePhoto } from '../utils/uploadSalePhoto';
import { uploadUnitRealVideo } from '../utils/uploadUnitRealVideo';
import UnitRealMediaPicker from './common/UnitRealMediaPicker';

function AddProduct() {
  const navigate = useNavigate();
  const [product, setProduct] = useState({
    name: '',
    description: '',
    category: '',
    brand: '',
    mrp: '',
    discountPercent: '',
    specs: {
      ramGB: '',
      storageGB: '',
      rearCameraMP: '',
      frontCameraMP: '',
      processor: '',
      batteryMah: '',
      network: '',
      displayInches: '',
      os: '',
      color: '',
      // Home appliances
      powerWatts: '',
      capacity: '',
      energyRating: '',
      // Old mobile specifics
      conditionGrade: '', // A+/A/B/C
      batteryHealth: '', // percentage
      ageMonths: '',
      purchaseYear: '',
      qcReport: {}, // QC Report for functional checks
      screenCondition: '', // Perfect/Minor Scratches/Cracked
      scratchesLevel: '', // None/Minor/Moderate/Heavy
      refurbished: false,
      warrantyMonths: '',
      hasBox: false,
      hasBill: false,
      hasCharger: false,
      hasEarphones: false,
      imei1: '',
      imei2: ''
    },
    emiAvailable: true,
    imageUrl: 'https://picsum.photos/300/300?random=1',
    variants: []
  });
  const [acquisition, setAcquisition] = useState({
    sellerName: '',
    sellerPhone: '',
    sellerAltPhone: '',
    sellerAddress: '',
    sellerNotes: '',
    acquisitionDate: new Date().toISOString().slice(0,10)
  });
  /** Old Mobile: seller se phone lene ki transaction photo (same API as Add Stock intake) */
  const [sellerIntakePhoto, setSellerIntakePhoto] = useState(null);
  const [unitMediaMode, setUnitMediaMode] = useState('none');
  const [unitPhotoFiles, setUnitPhotoFiles] = useState([]);
  const [unitVideoFile, setUnitVideoFile] = useState(null);
  
  const [variants, setVariants] = useState([]);
  const [imageUrl, setImageUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tagsText, setTagsText] = useState('');
  const [featuresText, setFeaturesText] = useState('');
  
  // Smart dropdown data
  const [ramOptions, setRamOptions] = useState([]);
  const [storageOptions, setStorageOptions] = useState([]);
  const [colorOptions, setColorOptions] = useState([]);
  const [sizeOptions, setSizeOptions] = useState([]);

  // Bulk variant creation states
  const [bulkVariantMode, setBulkVariantMode] = useState(false);
  const [bulkVariants, setBulkVariants] = useState([]);
  const [selectedRams, setSelectedRams] = useState([]);
  const [selectedStorages, setSelectedStorages] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);

  // Load saved options from localStorage on component mount
  useEffect(() => {
    loadSavedOptions();
  }, []);

  // Auto-calc discount when MRP/Price change
  useEffect(() => {
    const mrp = parseFloat(product.mrp);
    const price = parseFloat(product.price);
    if (!isNaN(mrp) && mrp > 0 && !isNaN(price) && price > 0) {
      const disc = Math.max(0, Math.min(100, Math.round(((mrp - price) / mrp) * 100)));
      if ((product.discountPercent || 0) !== disc) {
        setProduct(prev => ({ ...prev, discountPercent: disc }));
      }
    }
  }, [product.mrp, product.price]);

  // Initialize QC Report with all true when category is "Old Mobile"
  useEffect(() => {
    if (product.category === 'Old Mobile') {
      const defaultQCReport = buildDefaultUnitQcReport();
      
      // Only initialize if qcReport is empty or doesn't exist
      if (!product.specs.qcReport || Object.keys(product.specs.qcReport).length === 0) {
        setProduct(prev => ({
          ...prev,
          specs: {
            ...prev.specs,
            qcReport: defaultQCReport
          }
        }));
      } else {
        const merged = { ...defaultQCReport, ...product.specs.qcReport };
        if (
          product.specs.qcReport.backPanel === undefined &&
          product.specs.qcReport.bodyPanel !== undefined
        ) {
          merged.backPanel = product.specs.qcReport.bodyPanel;
        }
        const prevKeys = Object.keys(product.specs.qcReport).sort().join(',');
        const nextKeys = Object.keys(merged).sort().join(',');
        if (prevKeys !== nextKeys) {
          setProduct((prev) => ({
            ...prev,
            specs: { ...prev.specs, qcReport: merged }
          }));
        }
      }
    }
  }, [product.category]);

  const loadSavedOptions = () => {
    try {
      const savedRam = JSON.parse(localStorage.getItem('ramOptions') || '[]');
      const savedStorage = JSON.parse(localStorage.getItem('storageOptions') || '[]');
      const savedColors = JSON.parse(localStorage.getItem('colorOptions') || '[]');
      const savedSizes = JSON.parse(localStorage.getItem('sizeOptions') || '[]');
      
      setRamOptions(savedRam.length > 0 ? savedRam : ['2GB', '3GB', '4GB', '6GB', '8GB', '12GB', '16GB']);
      setStorageOptions(savedStorage.length > 0 ? savedStorage : ['16GB', '32GB', '64GB', '128GB', '256GB', '512GB', '1TB']);
      setColorOptions(savedColors.length > 0 ? savedColors : ['Black', 'White', 'Blue', 'Red', 'Green', 'Yellow', 'Purple', 'Pink', 'Gold', 'Silver']);
      setSizeOptions(savedSizes.length > 0 ? savedSizes : ['Small', 'Medium', 'Large', 'XL', 'XXL']);
    } catch (error) {
      console.error('Error loading saved options:', error);
    }
  };

  const saveOption = (type, value) => {
    if (!value.trim()) return;
    
    try {
      let currentOptions = [];
      let storageKey = '';
      
      switch (type) {
        case 'ram':
          currentOptions = [...ramOptions];
          storageKey = 'ramOptions';
          break;
        case 'storage':
          currentOptions = [...storageOptions];
          storageKey = 'storageOptions';
          break;
        case 'color':
          currentOptions = [...colorOptions];
          storageKey = 'colorOptions';
          break;
        case 'size':
          currentOptions = [...sizeOptions];
          storageKey = 'sizeOptions';
          break;
        default:
          return;
      }
      
      if (!currentOptions.includes(value)) {
        currentOptions.push(value);
        localStorage.setItem(storageKey, JSON.stringify(currentOptions));
        
        // Update state
        switch (type) {
          case 'ram':
            setRamOptions(currentOptions);
            break;
          case 'storage':
            setStorageOptions(currentOptions);
            break;
          case 'color':
            setColorOptions(currentOptions);
            break;
          case 'size':
            setSizeOptions(currentOptions);
            break;
        }
      }
    } catch (error) {
      console.error('Error saving option:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProduct({
      ...product,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSpecChange = (field, value) => {
    setProduct(prev => ({
      ...prev,
      specs: { 
        ...prev.specs, 
        [field]: value,
        // Initialize qcReport if not exists
        qcReport: prev.specs.qcReport || {}
      }
    }));
  };

  // Helper function for QC Report checkbox handling
  const handleQCCheck = (field, checked) => {
    const qcReport = product.specs.qcReport || {};
    if (checked) {
      qcReport[field] = true;
    } else {
      qcReport[field] = 'issue';
    }
    handleSpecChange('qcReport', qcReport);
  };

  // Helper function for QC Report issue text input
  const handleQCIssueText = (field, value) => {
    const qcReport = product.specs.qcReport || {};
    qcReport[field] = value || 'issue';
    handleSpecChange('qcReport', qcReport);
  };

  const handleAcqChange = (field, value) => {
    setAcquisition(prev => ({ ...prev, [field]: value }));
  };

  const handleAddImage = () => {
    if (imageUrl.trim()) {
      setProduct({
        ...product,
        imageUrl: imageUrl
      });
      setImageUrl('');
    }
  };

  const removeImage = () => {
    setProduct({
      ...product,
      imageUrl: 'https://picsum.photos/300/300?random=1'
    });
  };

  const addVariant = () => {
    setVariants([...variants, { name: '', value: '', customValue: '' }]);
  };

  const removeVariant = (index) => {
    const newVariants = [...variants];
    newVariants.splice(index, 1);
    setVariants(newVariants);
  };

  const handleVariantChange = (index, field, value) => {
    const newVariants = [...variants];
    newVariants[index][field] = value;
    
    // If it's a RAM+Storage variant, auto-generate the combined value
    if (field === 'ram' || field === 'storage') {
      const variant = newVariants[index];
      if (variant.ram && variant.storage) {
        variant.value = `${variant.ram}+${variant.storage}`;
        // Save the new RAM and Storage options
        saveOption('ram', variant.ram);
        saveOption('storage', variant.storage);
      }
    } else if (field === 'value') {
      // For other variant types, save the value
      const variant = newVariants[index];
      if (variant.name === 'Color') {
        saveOption('color', value);
      } else if (variant.name === 'Size') {
        saveOption('size', value);
      }
    }
    
    setVariants(newVariants);
  };

  // Bulk variant functions
  const generateBulkVariants = () => {
    if (selectedRams.length === 0 || selectedStorages.length === 0) {
      alert('Please select RAM and Storage options');
      return;
    }

    const variants = [];

    selectedRams.forEach((ram) => {
      selectedStorages.forEach((storage) => {
        const variantName = `${ram}+${storage}`;
        
        variants.push({
          name: 'RAM+Storage',
          value: variantName,
          price: '',
          buyingPrice: '',
          stock: 1,
          offerPrice: ''
        });
      });
    });

    setBulkVariants(variants);
  };

  const updateBulkVariant = (index, field, value) => {
    const newVariants = [...bulkVariants];
    newVariants[index][field] = value;
    setBulkVariants(newVariants);
  };

  const removeBulkVariant = (index) => {
    const newVariants = [...bulkVariants];
    newVariants.splice(index, 1);
    setBulkVariants(newVariants);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      let productsToCreate = [];

      const parseTags = (input) => {
        const separators = /[\n,;|]+|\s{2,}/g;
        return (input || '')
          .replace(/\u2022|\u00B7|\u2219/g, ' ')
          .split(separators)
          .map(t => t.trim())
          .filter(Boolean);
      };

      const parseFeatures = (input) => {
        return (input || '')
          .replace(/\u2022|\u00B7|\u2219/g, '\n')
          .replace(/,|;|\|/g, '\n')
          .split(/\n+/)
          .map(f => f.trim())
          .filter(Boolean);
      };

      const baseSpecs = { ...product.specs };
      const baseMeta = {
        brand: product.brand?.trim() || '',
        tags: parseTags(tagsText),
        features: parseFeatures(featuresText)
      };

      if (bulkVariantMode && bulkVariants.length > 0) {
        // Validate all required fields are filled
        const missingFields = bulkVariants.filter(variant => 
          !variant.price || !variant.buyingPrice || !variant.stock
        );
        
        if (missingFields.length > 0) {
          setError('Please fill in all required fields (Price, Buying Price, Stock) for all variants');
          setLoading(false);
          return;
        }

        // Create multiple products for bulk variants
        const parseRamStorage = (val) => {
          const m = (val || '').toLowerCase().match(/(\d+)\s*gb\s*\+\s*(\d+)\s*gb/);
          if (m) { return { ramGB: parseInt(m[1]), storageGB: parseInt(m[2]) }; }
          return { ramGB: undefined, storageGB: undefined };
        };

        productsToCreate = bulkVariants.map((variant, index) => ({
          name: `${product.name} ${variant.value}`,
          description: product.description || '',
          price: parseFloat(variant.price),
          buyingPrice: parseFloat(variant.buyingPrice),
          offerPrice: variant.offerPrice ? parseFloat(variant.offerPrice) : undefined,
          stock: parseInt(variant.stock),
          category: product.category,
          brand: baseMeta.brand,
          emiAvailable: true,
          imageUrl: product.imageUrl || 'https://picsum.photos/300/300?random=1',
          specs: {
            ...baseSpecs,
            ...parseRamStorage(variant.value)
          },
          tags: baseMeta.tags,
          features: baseMeta.features,
          variants: [{
            name: variant.name,
            value: variant.value
          }],
          emiOptions: [1, 2, 3, 4, 5, 6].map(months => ({
            months,
            interestRate: 0,
            downPayment: Math.round(parseFloat(variant.price) * 0.3)
          }))
        }));
      } else {
        // Single product with variants
      const formattedProduct = {
        ...product,
        description: product.description || '',
        price: parseFloat(product.price),
        buyingPrice: parseFloat(product.buyingPrice),
        offerPrice: product.offerPrice ? parseFloat(product.offerPrice) : undefined,
        stock: parseInt(product.stock),
        variants: variants.filter(v => v.name && v.value),
        emiAvailable: true,
          emiOptions: [1, 2, 3, 4, 5, 6].map(months => ({
        months,
        interestRate: 0,
            downPayment: Math.round(parseFloat(product.price) * 0.3)
          }))
        };
      formattedProduct.brand = baseMeta.brand;
      formattedProduct.specs = { ...baseSpecs };
      // Normalize numeric specs
      ['ramGB','storageGB','rearCameraMP','frontCameraMP','batteryMah','displayInches','powerWatts','energyRating']
        .forEach(k => {
          if (formattedProduct.specs && formattedProduct.specs[k] !== '' && formattedProduct.specs[k] !== undefined) {
            const num = k === 'displayInches' ? parseFloat(formattedProduct.specs[k]) : parseInt(formattedProduct.specs[k]);
            formattedProduct.specs[k] = isNaN(num) ? undefined : num;
          }
        });
      formattedProduct.tags = baseMeta.tags;
      formattedProduct.features = baseMeta.features;

      // Attach acquisition details for Old Mobile
      if (product.category === 'Old Mobile') {
        formattedProduct.acquiredFrom = { ...acquisition };
      }
      
      if (!formattedProduct.imageUrl || formattedProduct.imageUrl.trim() === '') {
          formattedProduct.imageUrl = 'https://picsum.photos/300/300?random=1';
        }
        
        productsToCreate = [formattedProduct];
      }
      
      console.log('Submitting products:', productsToCreate);

      for (const productData of productsToCreate) {
        if (
          productData.category === 'Old Mobile' &&
          (productData.specs?.imei1 || product.specs?.imei1)
        ) {
          const rawPhone = String(acquisition.sellerPhone || '').trim();
          const digits = rawPhone.replace(/\D/g, '');
          if (rawPhone && digits.length < 10) {
            setError('Old Mobile: Seller mobile agar bhara hai to 10 digit likhein');
            setLoading(false);
            return;
          }
        }
      }
      
      // Create all products
      for (const productData of productsToCreate) {
        // Automatically add branchId for Punjab branch
        const productWithBranch = addBranchToBody(productData);
        const response = await axios.post(`${config.API_URL}/products`, productWithBranch);
        
        // If it's a mobile device and has IMEI, add it to imeiTracking
        if ((productData.category === 'Mobile' || productData.category === 'Old Mobile') && 
            (productData.specs.imei1 || product.specs.imei1)) {
          const imei1 = productData.specs.imei1 || product.specs.imei1;
          const imei2 = productData.specs.imei2 || product.specs.imei2;
          
          if (imei1) {
            try {
              const imeiPayload = {
                imei1: imei1,
                imei2: imei2 || ''
              };
              // Old Mobile: send seller + buying price so backend can WhatsApp thank-you (same as Inventory add-stock)
              if (productData.category === 'Old Mobile') {
                const sellerName = String(acquisition.sellerName || '').trim();
                const sellerPhone = String(acquisition.sellerPhone || '').trim();
                if (sellerName || sellerPhone) {
                  imeiPayload.unitSeller = { sellerName, sellerPhone };
                }
              }
              const addImeiRes = await axios.post(
                `${config.API_URL}/products/${response.data._id}/imei`,
                imeiPayload
              );
              const imeiIndex = addImeiRes.data?.imeiIndex;
              const si = addImeiRes.data?.sellerIntake;
              if (si?.awarded) {
                console.log('Seller intake: +100 points, WhatsApp queued', si);
              } else if (productData.category === 'Old Mobile' && acquisition.sellerPhone?.trim()) {
                console.warn('Seller intake not awarded:', si);
              }
              if (
                productData.category === 'Old Mobile' &&
                sellerIntakePhoto &&
                imeiIndex != null
              ) {
                let sellerCustomerId = '';
                const ph = String(acquisition.sellerPhone || '').replace(/\D/g, '').slice(-10);
                if (ph.length === 10) {
                  try {
                    const cr = await axios.get(`${config.API_URL}/customers`, { params: { phone: ph } });
                    if (cr.data?.customer?._id) sellerCustomerId = cr.data.customer._id;
                  } catch (_) {
                    /* optional */
                  }
                }
                await uploadSalePhoto(sellerIntakePhoto, {
                  kind: 'seller_intake',
                  productId: response.data._id,
                  imeiIndex,
                  customerId: sellerCustomerId
                });
              }
              if (productData.category === 'Old Mobile' && imeiIndex != null) {
                const photos = unitMediaMode === 'photos' ? unitPhotoFiles : [];
                if (photos.length > 0) {
                  const fd = new FormData();
                  photos.slice(0, 3).forEach((f) => fd.append('photos', f));
                  await axios.post(
                    `${config.API_URL}/products/${response.data._id}/imei/${imeiIndex}/unit-real-photos`,
                    fd,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                  );
                }
                if (unitMediaMode === 'video' && unitVideoFile) {
                  const videoUpload = await uploadUnitRealVideo(unitVideoFile, {
                    productId: response.data._id,
                    imeiIndex
                  });
                  if (!videoUpload.success) {
                    throw new Error(videoUpload.error || 'Unit video upload failed');
                  }
                }
              }
              console.log('IMEI added successfully for product:', response.data._id);
            } catch (imeiError) {
              console.error('Error adding IMEI:', imeiError);
              throw imeiError;
            }
          }
        }
      }
      
      alert(`Successfully added ${productsToCreate.length} product(s)!`);
      navigate('/');
    } catch (err) {
      setError('Failed to add product(s): ' + (err.response?.data?.message || err.message));
      console.error('Error adding product(s):', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="mb-4">Add New Product</h2>
      
      {error && (
        <div className="alert alert-danger">{error}</div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label htmlFor="name" className="form-label">Product Name</label>
          <input
            type="text"
            className="form-control"
            id="name"
            name="name"
            value={product.name}
            onChange={handleChange}
            required
          />
        </div>
        
        <div className="mb-3">
          <label htmlFor="description" className="form-label">Description (Optional)</label>
          <textarea
            className="form-control"
            id="description"
            name="description"
            rows="3"
            value={product.description}
            onChange={handleChange}
          ></textarea>
        </div>
        
        <div className="mb-3">
          <label htmlFor="category" className="form-label">Category</label>
          <select
            className="form-control"
            id="category"
            name="category"
            value={product.category}
            onChange={handleChange}
            required
          >
            <option value="">Select Category</option>
            <option value="Mobile">📱 Mobile</option>
            <option value="Old Mobile">📲 Old Mobile (Premium)</option>
            <option value="Speaker">🔊 Speaker</option>
            <option value="Home Theatre">🎬 Home Theatre</option>
            <option value="Soundbar">📢 Soundbar</option>
            <option value="Neckband">🎧 Neckband</option>
            <option value="Airpods">🎵 Airpods</option>
            <option value="Smartwatches">⌚ Smartwatches</option>
            <option value="TV">📺 TV</option>
            <option value="Refrigerator">❄️ Refrigerator</option>
            <option value="Washing Machine">🧺 Washing Machine</option>
            <option value="AC">❄️ AC</option>
            <option value="FAN">💨 FAN</option>
            <option value="Microwave">🔥 Microwave</option>
            <option value="Mixer/Juicer">🥤 Mixer/Juicer</option>
            <option value="Other Accessories">🔧 Other Accessories</option>
          </select>
        </div>

        {/* Old Mobile specific form */}
        {product.category === 'Old Mobile' && (
          <div className="card mb-4">
            <div className="card-header d-flex justify-content-between align-items-center">
              <strong>Old Mobile Details</strong>
              {product.discountPercent && (
                <span className="badge bg-success">Discount: {product.discountPercent}%</span>
              )}
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-3 mb-3">
                  <label className="form-label">MRP (₹)</label>
                  <input type="number" className="form-control" name="mrp" value={product.mrp} onChange={handleChange} />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Selling Price (₹)</label>
                  <input type="number" className="form-control" name="price" value={product.price || ''} onChange={handleChange} />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Buying Price (₹)</label>
                  <input type="number" className="form-control" name="buyingPrice" value={product.buyingPrice || ''} onChange={handleChange} />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Discount (%)</label>
                  <input type="number" className="form-control" name="discountPercent" value={product.discountPercent || ''} onChange={handleChange} />
                </div>
              </div>
              <div className="row">
                <div className="col-md-3 mb-3">
                  <label className="form-label">Condition Grade</label>
                  <select className="form-select" value={product.specs.conditionGrade} onChange={(e)=>handleSpecChange('conditionGrade', e.target.value)}>
                    <option value="">Select</option>
                    {OLD_MOBILE_GRADE_OPTIONS.map((g) => (
                      <option key={g.value} value={g.value}>{g.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Battery Health (%)</label>
                  <input type="number" className="form-control" value={product.specs.batteryHealth} onChange={(e)=>handleSpecChange('batteryHealth', e.target.value)} />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Age (Months)</label>
                  <input type="number" className="form-control" value={product.specs.ageMonths} onChange={(e)=>handleSpecChange('ageMonths', e.target.value)} />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Purchase Year</label>
                  <input type="number" className="form-control" value={product.specs.purchaseYear} onChange={(e)=>handleSpecChange('purchaseYear', e.target.value)} />
                </div>
              </div>
              <div className="row">
                <div className="col-md-3 mb-3">
                  <label className="form-label">Screen Condition</label>
                  <select className="form-select" value={product.specs.screenCondition} onChange={(e)=>handleSpecChange('screenCondition', e.target.value)}>
                    <option value="">Select</option>
                    <option value="Perfect">Perfect</option>
                    <option value="Minor Scratches">Minor Scratches</option>
                    <option value="Moderate Scratches">Moderate Scratches</option>
                    <option value="Cracked">Cracked</option>
                  </select>
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Scratches Level</label>
                  <select className="form-select" value={product.specs.scratchesLevel} onChange={(e)=>handleSpecChange('scratchesLevel', e.target.value)}>
                    <option value="">Select</option>
                    <option value="None">None</option>
                    <option value="Minor">Minor</option>
                    <option value="Moderate">Moderate</option>
                    <option value="Heavy">Heavy</option>
                  </select>
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Warranty (Months)</label>
                  <input type="number" className="form-control" value={product.specs.warrantyMonths} onChange={(e)=>handleSpecChange('warrantyMonths', e.target.value)} />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Refurbished</label>
                  <select className="form-select" value={product.specs.refurbished ? 'yes':'no'} onChange={(e)=>handleSpecChange('refurbished', e.target.value==='yes')}>
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>
              </div>
              <div className="row">
                <div className="col-md-3 mb-3">
                  <label className="form-label">IMEI 1</label>
                  <input type="text" className="form-control" value={product.specs.imei1} onChange={(e)=>handleSpecChange('imei1', e.target.value)} />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">IMEI 2 (optional)</label>
                  <input type="text" className="form-control" value={product.specs.imei2} onChange={(e)=>handleSpecChange('imei2', e.target.value)} />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Accessories</label>
                  <div className="d-flex flex-wrap gap-3">
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" id="accBox" checked={product.specs.hasBox} onChange={(e)=>handleSpecChange('hasBox', e.target.checked)} />
                      <label className="form-check-label" htmlFor="accBox">Original Box</label>
                    </div>
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" id="accBill" checked={product.specs.hasBill} onChange={(e)=>handleSpecChange('hasBill', e.target.checked)} />
                      <label className="form-check-label" htmlFor="accBill">Original Bill</label>
                    </div>
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" id="accCharger" checked={product.specs.hasCharger} onChange={(e)=>handleSpecChange('hasCharger', e.target.checked)} />
                      <label className="form-check-label" htmlFor="accCharger">Charger</label>
                    </div>
                    <div className="form-check">
                      <input className="form-check-input" type="checkbox" id="accEar" checked={product.specs.hasEarphones} onChange={(e)=>handleSpecChange('hasEarphones', e.target.checked)} />
                      <label className="form-check-label" htmlFor="accEar">Earphones</label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="alert alert-info py-2">
                <small>
                  Tip: Use Condition, Battery Health and Accessories to decide fair price. Discount auto-calculates from MRP and Selling Price.
                </small>
              </div>
            </div>
          </div>
        )}

        {/* QC Report Section - For Old Mobiles */}
        {product.category === 'Old Mobile' && (
          <div className="card mb-4">
            <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
              <div>
                <strong>🔍 QC Report (Quality Check)</strong>
                <small className="ms-2">Tick all working features, mark issues in red</small>
              </div>
              <button 
                type="button"
                className="btn btn-sm btn-light"
                onClick={() => {
                  const qcReport = buildDefaultUnitQcReport();
                  setProduct(prev => ({
                    ...prev,
                    specs: {
                      ...prev.specs,
                      qcReport: qcReport
                    }
                  }));
                }}
              >
                ✓ Tick All
              </button>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <div className="qc-check-item">
                    <label className="form-label d-flex align-items-center">
                      <input 
                        type="checkbox" 
                        className="form-check-input me-2" 
                        checked={product.specs.qcReport?.display === true || product.specs.qcReport?.display === 'working'}
                        onChange={(e) => handleQCCheck('display', e.target.checked)}
                      />
                      <span>Display Screen</span>
                    </label>
                    {product.specs.qcReport?.display && product.specs.qcReport?.display !== true && (
                      <input 
                        type="text" 
                        className="form-control mt-1" 
                        placeholder="Issue details (e.g., Dead pixels, Cracked)"
                        value={typeof product.specs.qcReport?.display === 'string' ? product.specs.qcReport.display : ''}
                        onChange={(e) => handleQCIssueText('display', e.target.value)}
                      />
                    )}
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="qc-check-item">
                    <label className="form-label d-flex align-items-center">
                      <input 
                        type="checkbox" 
                        className="form-check-input me-2" 
                        checked={product.specs.qcReport?.touch === true || product.specs.qcReport?.touch === 'working'}
                        onChange={(e) => handleQCCheck('touch', e.target.checked)}
                      />
                      <span>Touch Function</span>
                    </label>
                    {product.specs.qcReport?.touch && product.specs.qcReport?.touch !== true && (
                      <input 
                        type="text" 
                        className="form-control mt-1" 
                        placeholder="Issue details (e.g., Not responding, Partial)"
                        value={typeof product.specs.qcReport?.touch === 'string' ? product.specs.qcReport.touch : ''}
                        onChange={(e) => handleQCIssueText('touch', e.target.value)}
                      />
                    )}
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="qc-check-item">
                    <label className="form-label d-flex align-items-center">
                      <input 
                        type="checkbox" 
                        className="form-check-input me-2" 
                        checked={product.specs.qcReport?.rearCamera === true || product.specs.qcReport?.rearCamera === 'working'}
                        onChange={(e) => handleQCCheck('rearCamera', e.target.checked)}
                      />
                      <span>Rear Camera</span>
                    </label>
                    {product.specs.qcReport?.rearCamera && product.specs.qcReport?.rearCamera !== true && (
                      <input 
                        type="text" 
                        className="form-control mt-1" 
                        placeholder="Issue details (e.g., Blurry, Not working)"
                        value={typeof product.specs.qcReport?.rearCamera === 'string' ? product.specs.qcReport.rearCamera : ''}
                        onChange={(e) => handleQCIssueText('rearCamera', e.target.value)}
                      />
                    )}
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="qc-check-item">
                    <label className="form-label d-flex align-items-center">
                      <input 
                        type="checkbox" 
                        className="form-check-input me-2" 
                        checked={product.specs.qcReport?.frontCamera === true || product.specs.qcReport?.frontCamera === 'working'}
                        onChange={(e) => handleQCCheck('frontCamera', e.target.checked)}
                      />
                      <span>Front Camera</span>
                    </label>
                    {product.specs.qcReport?.frontCamera && product.specs.qcReport?.frontCamera !== true && (
                      <input 
                        type="text" 
                        className="form-control mt-1" 
                        placeholder="Issue details (e.g., Blurry, Not working)"
                        value={typeof product.specs.qcReport?.frontCamera === 'string' ? product.specs.qcReport.frontCamera : ''}
                        onChange={(e) => handleQCIssueText('frontCamera', e.target.value)}
                      />
                    )}
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="qc-check-item">
                    <label className="form-label d-flex align-items-center">
                      <input 
                        type="checkbox" 
                        className="form-check-input me-2" 
                        checked={product.specs.qcReport?.speaker === true || product.specs.qcReport?.speaker === 'working'}
                        onChange={(e) => handleQCCheck('speaker', e.target.checked)}
                      />
                      <span>Speaker</span>
                    </label>
                    {product.specs.qcReport?.speaker && product.specs.qcReport?.speaker !== true && (
                      <input 
                        type="text" 
                        className="form-control mt-1" 
                        placeholder="Issue details (e.g., Low volume, Distorted)"
                        value={typeof product.specs.qcReport?.speaker === 'string' ? product.specs.qcReport.speaker : ''}
                        onChange={(e) => handleQCIssueText('speaker', e.target.value)}
                      />
                    )}
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="qc-check-item">
                    <label className="form-label d-flex align-items-center">
                      <input 
                        type="checkbox" 
                        className="form-check-input me-2" 
                        checked={product.specs.qcReport?.microphone === true || product.specs.qcReport?.microphone === 'working'}
                        onChange={(e) => handleQCCheck('microphone', e.target.checked)}
                      />
                      <span>Microphone</span>
                    </label>
                    {product.specs.qcReport?.microphone && product.specs.qcReport?.microphone !== true && (
                      <input 
                        type="text" 
                        className="form-control mt-1" 
                        placeholder="Issue details (e.g., Not working, Low volume)"
                        value={typeof product.specs.qcReport?.microphone === 'string' ? product.specs.qcReport.microphone : ''}
                        onChange={(e) => handleQCIssueText('microphone', e.target.value)}
                      />
                    )}
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="qc-check-item">
                    <label className="form-label d-flex align-items-center">
                      <input 
                        type="checkbox" 
                        className="form-check-input me-2" 
                        checked={product.specs.qcReport?.wifi === true || product.specs.qcReport?.wifi === 'working'}
                        onChange={(e) => handleQCCheck('wifi', e.target.checked)}
                      />
                      <span>WiFi</span>
                    </label>
                    {product.specs.qcReport?.wifi && product.specs.qcReport?.wifi !== true && (
                      <input 
                        type="text" 
                        className="form-control mt-1" 
                        placeholder="Issue details (e.g., Not connecting, Slow)"
                        value={typeof product.specs.qcReport?.wifi === 'string' ? product.specs.qcReport.wifi : ''}
                        onChange={(e) => handleQCIssueText('wifi', e.target.value)}
                      />
                    )}
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="qc-check-item">
                    <label className="form-label d-flex align-items-center">
                      <input 
                        type="checkbox" 
                        className="form-check-input me-2" 
                        checked={product.specs.qcReport?.bluetooth === true || product.specs.qcReport?.bluetooth === 'working'}
                        onChange={(e) => handleQCCheck('bluetooth', e.target.checked)}
                      />
                      <span>Bluetooth</span>
                    </label>
                    {product.specs.qcReport?.bluetooth && product.specs.qcReport?.bluetooth !== true && (
                      <input 
                        type="text" 
                        className="form-control mt-1" 
                        placeholder="Issue details (e.g., Not pairing, Weak signal)"
                        value={typeof product.specs.qcReport?.bluetooth === 'string' ? product.specs.qcReport.bluetooth : ''}
                        onChange={(e) => handleQCIssueText('bluetooth', e.target.value)}
                      />
                    )}
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="qc-check-item">
                    <label className="form-label d-flex align-items-center">
                      <input 
                        type="checkbox" 
                        className="form-check-input me-2" 
                        checked={product.specs.qcReport?.network === true || product.specs.qcReport?.network === 'working'}
                        onChange={(e) => handleQCCheck('network', e.target.checked)}
                      />
                      <span>Network/Sim</span>
                    </label>
                    {product.specs.qcReport?.network && product.specs.qcReport?.network !== true && (
                      <input 
                        type="text" 
                        className="form-control mt-1" 
                        placeholder="Issue details (e.g., No signal, Sim not detected)"
                        value={typeof product.specs.qcReport?.network === 'string' ? product.specs.qcReport.network : ''}
                        onChange={(e) => handleQCIssueText('network', e.target.value)}
                      />
                    )}
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="qc-check-item">
                    <label className="form-label d-flex align-items-center">
                      <input 
                        type="checkbox" 
                        className="form-check-input me-2" 
                        checked={product.specs.qcReport?.charging === true || product.specs.qcReport?.charging === 'working'}
                        onChange={(e) => handleQCCheck('charging', e.target.checked)}
                      />
                      <span>Charging Port</span>
                    </label>
                    {product.specs.qcReport?.charging && product.specs.qcReport?.charging !== true && (
                      <input 
                        type="text" 
                        className="form-control mt-1" 
                        placeholder="Issue details (e.g., Loose, Not charging)"
                        value={typeof product.specs.qcReport?.charging === 'string' ? product.specs.qcReport.charging : ''}
                        onChange={(e) => handleQCIssueText('charging', e.target.value)}
                      />
                    )}
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="qc-check-item">
                    <label className="form-label d-flex align-items-center">
                      <input 
                        type="checkbox" 
                        className="form-check-input me-2" 
                        checked={product.specs.qcReport?.fingerprint === true || product.specs.qcReport?.fingerprint === 'working'}
                        onChange={(e) => handleQCCheck('fingerprint', e.target.checked)}
                      />
                      <span>Fingerprint Sensor</span>
                    </label>
                    {product.specs.qcReport?.fingerprint && product.specs.qcReport?.fingerprint !== true && (
                      <input 
                        type="text" 
                        className="form-control mt-1" 
                        placeholder="Issue details (e.g., Not working, Slow)"
                        value={typeof product.specs.qcReport?.fingerprint === 'string' ? product.specs.qcReport.fingerprint : ''}
                        onChange={(e) => handleQCIssueText('fingerprint', e.target.value)}
                      />
                    )}
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="qc-check-item">
                    <label className="form-label d-flex align-items-center">
                      <input 
                        type="checkbox" 
                        className="form-check-input me-2" 
                        checked={product.specs.qcReport?.faceUnlock === true || product.specs.qcReport?.faceUnlock === 'working'}
                        onChange={(e) => handleQCCheck('faceUnlock', e.target.checked)}
                      />
                      <span>Face Unlock</span>
                    </label>
                    {product.specs.qcReport?.faceUnlock && product.specs.qcReport?.faceUnlock !== true && (
                      <input 
                        type="text" 
                        className="form-control mt-1" 
                        placeholder="Issue details (e.g., Not working, Slow)"
                        value={typeof product.specs.qcReport?.faceUnlock === 'string' ? product.specs.qcReport.faceUnlock : ''}
                        onChange={(e) => handleQCIssueText('faceUnlock', e.target.value)}
                      />
                    )}
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="qc-check-item">
                    <label className="form-label d-flex align-items-center">
                      <input 
                        type="checkbox" 
                        className="form-check-input me-2" 
                        checked={product.specs.qcReport?.buttons === true || product.specs.qcReport?.buttons === 'working'}
                        onChange={(e) => handleQCCheck('buttons', e.target.checked)}
                      />
                      <span>Physical Buttons</span>
                    </label>
                    {product.specs.qcReport?.buttons && product.specs.qcReport?.buttons !== true && (
                      <input 
                        type="text" 
                        className="form-control mt-1" 
                        placeholder="Issue details (e.g., Volume button stuck, Power button not working)"
                        value={typeof product.specs.qcReport?.buttons === 'string' ? product.specs.qcReport.buttons : ''}
                        onChange={(e) => handleQCIssueText('buttons', e.target.value)}
                      />
                    )}
                  </div>
                </div>
                {IMEI_QC_KEYS.slice(13).map(({ key, label }) => (
                  <div key={key} className="col-md-6 mb-3">
                    <div className="qc-check-item">
                      <label className="form-label d-flex align-items-center">
                        <input
                          type="checkbox"
                          className="form-check-input me-2"
                          checked={
                            product.specs.qcReport?.[key] === true ||
                            product.specs.qcReport?.[key] === 'working' ||
                            (key === 'backPanel' &&
                              product.specs.qcReport?.backPanel === undefined &&
                              (product.specs.qcReport?.bodyPanel === true ||
                                product.specs.qcReport?.bodyPanel === 'working'))
                          }
                          onChange={(e) => handleQCCheck(key, e.target.checked)}
                        />
                        <span>{label}</span>
                      </label>
                      {product.specs.qcReport?.[key] &&
                        product.specs.qcReport?.[key] !== true && (
                          <input
                            type="text"
                            className="form-control mt-1"
                            placeholder="Issue details"
                            value={
                              typeof product.specs.qcReport?.[key] === 'string'
                                ? product.specs.qcReport[key]
                                : ''
                            }
                            onChange={(e) => handleQCIssueText(key, e.target.value)}
                          />
                        )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="alert alert-success py-2">
                <small>
                  <strong>💡 Tip:</strong> Tick all working features. If any feature has issues, uncheck and add details. Customer will see this QC Report clearly.
                </small>
              </div>
            </div>
          </div>
        )}

        {product.category === 'Old Mobile' && (
          <div className="card mb-4">
            <div className="card-header"><strong>Acquisition Details (Seller)</strong></div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label">Seller Name <span className="text-muted">(optional)</span></label>
                  <input type="text" className="form-control" value={acquisition.sellerName} onChange={(e)=>handleAcqChange('sellerName', e.target.value)} />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Seller Phone <span className="text-muted">(optional)</span></label>
                  <input type="text" className="form-control" value={acquisition.sellerPhone} onChange={(e)=>handleAcqChange('sellerPhone', e.target.value)} />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Alt Phone (optional)</label>
                  <input type="text" className="form-control" value={acquisition.sellerAltPhone} onChange={(e)=>handleAcqChange('sellerAltPhone', e.target.value)} />
                </div>
              </div>
              <div className="row">
                <div className="col-md-8 mb-3">
                  <label className="form-label">Address</label>
                  <input type="text" className="form-control" value={acquisition.sellerAddress} onChange={(e)=>handleAcqChange('sellerAddress', e.target.value)} />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Deal Date</label>
                  <input type="date" className="form-control" value={acquisition.acquisitionDate} onChange={(e)=>handleAcqChange('acquisitionDate', e.target.value)} />
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label">Notes</label>
                <textarea className="form-control" rows="2" value={acquisition.sellerNotes} onChange={(e)=>handleAcqChange('sellerNotes', e.target.value)} placeholder="Any remarks about the deal, condition, etc."></textarea>
              </div>
              <OptionalPhotoUpload
                title="Seller / shop intake photo"
                hint="Customer ne shop ko phone bechte waqt — wahi photo Add Stock wale flow jaisi (Sold to Shop profile me)."
                file={sellerIntakePhoto}
                onChange={setSellerIntakePhoto}
                className="mb-0"
              />
            </div>
          </div>
        )}

        {/* Brand & Specs (contextual) */}
        <div className="mb-3">
          <label className="form-label">Brand</label>
          <input
            type="text"
            className="form-control"
            name="brand"
            value={product.brand}
            onChange={handleChange}
            placeholder="e.g., Samsung, Apple, Realme"
          />
        </div>


        {(product.category === 'Mobile' || product.category === 'Old Mobile') && (
          <div className="card mb-4">
            <div className="card-header">
              <strong>IMEI Information</strong>
              <small className="text-muted ms-2">(Each IMEI = 1 unit of this model)</small>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">IMEI 1 (Primary) *</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    value={product.specs.imei1} 
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 15);
                      handleSpecChange('imei1', value);
                    }}
                    placeholder="15 digit IMEI number"
                    maxLength="15"
                    required
                  />
                  <small className="text-muted">Enter 15-digit IMEI number</small>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">IMEI 2 (Secondary)</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    value={product.specs.imei2} 
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 15);
                      handleSpecChange('imei2', value);
                    }}
                    placeholder="15 digit IMEI number (if dual SIM)"
                    maxLength="15"
                  />
                  <small className="text-muted">For dual SIM phones only</small>
                </div>
              </div>
              <div className="alert alert-info">
                <i className="bi bi-info-circle me-2"></i>
                <strong>Note:</strong> Each IMEI represents 1 unit. If you have multiple units of same model, create separate products with different IMEIs.
              </div>
              {product.category === 'Old Mobile' && (
                <>
                  <div className="alert alert-light border mb-3 small">
                    <strong>Seller / deal (isme dikhega IMEI inventory me):</strong>{' '}
                    {acquisition.sellerName?.trim() ? (
                      <>
                        <span>{acquisition.sellerName.trim()}</span>
                        {acquisition.sellerPhone?.trim() ? (
                          <span className="text-muted"> · {acquisition.sellerPhone.trim()}</span>
                        ) : null}
                      </>
                    ) : (
                      <span className="text-muted">Upar card &quot;Acquisition Details (Seller)&quot; mein naam bharein</span>
                    )}
                  </div>
                  <UnitRealMediaPicker
                    mediaMode={unitMediaMode}
                    onMediaModeChange={setUnitMediaMode}
                    photoFiles={unitPhotoFiles}
                    onPhotoFilesChange={setUnitPhotoFiles}
                    videoFile={unitVideoFile}
                    onVideoFileChange={setUnitVideoFile}
                  />
                </>
              )}
            </div>
          </div>
        )}

        {(product.category === 'Mobile' || product.category === 'Smartwatches') && (
          <div className="card mb-4">
            <div className="card-header"><strong>Technical Specs</strong></div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-3 mb-3">
                  <label className="form-label">RAM (GB)</label>
                  <input type="number" className="form-control" value={product.specs.ramGB} onChange={(e) => handleSpecChange('ramGB', e.target.value)} />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Storage (GB)</label>
                  <input type="number" className="form-control" value={product.specs.storageGB} onChange={(e) => handleSpecChange('storageGB', e.target.value)} />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Rear Camera (MP)</label>
                  <input type="number" className="form-control" value={product.specs.rearCameraMP} onChange={(e) => handleSpecChange('rearCameraMP', e.target.value)} />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Front Camera (MP)</label>
                  <input type="number" className="form-control" value={product.specs.frontCameraMP} onChange={(e) => handleSpecChange('frontCameraMP', e.target.value)} />
                </div>
              </div>
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label">Processor</label>
                  <input type="text" className="form-control" value={product.specs.processor} onChange={(e) => handleSpecChange('processor', e.target.value)} />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Battery (mAh)</label>
                  <input type="number" className="form-control" value={product.specs.batteryMah} onChange={(e) => handleSpecChange('batteryMah', e.target.value)} />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Display (inches)</label>
                  <input type="number" step="0.1" className="form-control" value={product.specs.displayInches} onChange={(e) => handleSpecChange('displayInches', e.target.value)} />
                </div>
              </div>
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label">Network</label>
                  <select className="form-select" value={product.specs.network} onChange={(e) => handleSpecChange('network', e.target.value)}>
                    <option value="">Select</option>
                    <option value="4G">4G</option>
                    <option value="5G">5G</option>
                    <option value="WiFi Only">WiFi Only</option>
                  </select>
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">OS</label>
                  <input type="text" className="form-control" value={product.specs.os} onChange={(e) => handleSpecChange('os', e.target.value)} />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Color</label>
                  <input type="text" className="form-control" value={product.specs.color} onChange={(e) => handleSpecChange('color', e.target.value)} />
                </div>
              </div>
            </div>
          </div>
        )}

        {product.category === 'TV' && (
          <div className="card mb-4">
            <div className="card-header"><strong>TV Specs</strong></div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Screen Type</label>
                  <input type="text" className="form-control" value={product.specs.screenType || ''} onChange={(e) => handleSpecChange('screenType', e.target.value)} />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Resolution</label>
                  <input type="text" className="form-control" value={product.specs.resolution || ''} onChange={(e) => handleSpecChange('resolution', e.target.value)} />
                </div>
              </div>
            </div>
          </div>
        )}

        {product.category === 'Old Mobile' && (
          <div className="card mb-4">
            <div className="card-header"><strong>Old Mobile Specs</strong></div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-3 mb-3">
                  <label className="form-label">RAM (GB)</label>
                  <input type="number" className="form-control" value={product.specs.ramGB} onChange={(e) => handleSpecChange('ramGB', e.target.value)} />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Storage (GB)</label>
                  <input type="number" className="form-control" value={product.specs.storageGB} onChange={(e) => handleSpecChange('storageGB', e.target.value)} />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Rear Camera (MP)</label>
                  <input type="number" className="form-control" value={product.specs.rearCameraMP} onChange={(e) => handleSpecChange('rearCameraMP', e.target.value)} />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Front Camera (MP)</label>
                  <input type="number" className="form-control" value={product.specs.frontCameraMP} onChange={(e) => handleSpecChange('frontCameraMP', e.target.value)} />
                </div>
              </div>
              <div className="row">
                <div className="col-md-3 mb-3">
                  <label className="form-label">Processor</label>
                  <input type="text" className="form-control" value={product.specs.processor} onChange={(e) => handleSpecChange('processor', e.target.value)} />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Battery (mAh)</label>
                  <input type="number" className="form-control" value={product.specs.batteryMah} onChange={(e) => handleSpecChange('batteryMah', e.target.value)} />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Display (inches)</label>
                  <input type="number" step="0.1" className="form-control" value={product.specs.displayInches} onChange={(e) => handleSpecChange('displayInches', e.target.value)} />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">OS</label>
                  <input type="text" className="form-control" value={product.specs.os} onChange={(e) => handleSpecChange('os', e.target.value)} />
                </div>
              </div>
              <div className="row">
                <div className="col-md-3 mb-3">
                  <label className="form-label">Color</label>
                  <input type="text" className="form-control" value={product.specs.color} onChange={(e) => handleSpecChange('color', e.target.value)} />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Condition Grade</label>
                  <select className="form-select" value={product.specs.conditionGrade} onChange={(e) => handleSpecChange('conditionGrade', e.target.value)}>
                    <option value="">Select</option>
                    {OLD_MOBILE_GRADE_OPTIONS.map((g) => (
                      <option key={g.value} value={g.value}>{g.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Battery Health (%)</label>
                  <input type="number" min="0" max="100" className="form-control" value={product.specs.batteryHealth} onChange={(e) => handleSpecChange('batteryHealth', e.target.value)} />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Age (Months)</label>
                  <input type="number" className="form-control" value={product.specs.ageMonths} onChange={(e) => handleSpecChange('ageMonths', e.target.value)} />
                </div>
              </div>
              <div className="row">
                <div className="col-md-3 mb-3">
                  <label className="form-label">Purchase Year</label>
                  <input type="number" className="form-control" value={product.specs.purchaseYear} onChange={(e) => handleSpecChange('purchaseYear', e.target.value)} />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Screen Condition</label>
                  <input type="text" className="form-control" value={product.specs.screenCondition} onChange={(e) => handleSpecChange('screenCondition', e.target.value)} placeholder="Perfect/Minor Scratches/Cracked" />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Scratches Level</label>
                  <select className="form-select" value={product.specs.scratchesLevel} onChange={(e) => handleSpecChange('scratchesLevel', e.target.value)}>
                    <option value="">Select</option>
                    <option value="None">None</option>
                    <option value="Minor">Minor</option>
                    <option value="Moderate">Moderate</option>
                    <option value="Heavy">Heavy</option>
                  </select>
                </div>
                <div className="col-md-3 mb-3">
                  <label className="form-label">Warranty (Months)</label>
                  <input type="number" className="form-control" value={product.specs.warrantyMonths} onChange={(e) => handleSpecChange('warrantyMonths', e.target.value)} />
                </div>
              </div>
              <div className="row">
                <div className="col-md-12 mb-3">
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" checked={product.specs.refurbished} onChange={(e) => handleSpecChange('refurbished', e.target.checked)} />
                    <label className="form-check-label">Refurbished</label>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-3 mb-3">
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" checked={product.specs.hasBox} onChange={(e) => handleSpecChange('hasBox', e.target.checked)} />
                    <label className="form-check-label">Has Box</label>
                  </div>
                </div>
                <div className="col-md-3 mb-3">
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" checked={product.specs.hasBill} onChange={(e) => handleSpecChange('hasBill', e.target.checked)} />
                    <label className="form-check-label">Has Bill</label>
                  </div>
                </div>
                <div className="col-md-3 mb-3">
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" checked={product.specs.hasCharger} onChange={(e) => handleSpecChange('hasCharger', e.target.checked)} />
                    <label className="form-check-label">Has Charger</label>
                  </div>
                </div>
                <div className="col-md-3 mb-3">
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" checked={product.specs.hasEarphones} onChange={(e) => handleSpecChange('hasEarphones', e.target.checked)} />
                    <label className="form-check-label">Has Earphones</label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {(product.category === 'Refrigerator' || product.category === 'AC' || product.category === 'FAN') && (
          <div className="card mb-4">
            <div className="card-header"><strong>Home Appliance Specs</strong></div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label">Power (Watts)</label>
                  <input type="number" className="form-control" value={product.specs.powerWatts} onChange={(e) => handleSpecChange('powerWatts', e.target.value)} />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Capacity</label>
                  <input type="text" className="form-control" value={product.specs.capacity} onChange={(e) => handleSpecChange('capacity', e.target.value)} />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Energy Rating (1-5)</label>
                  <select className="form-select" value={product.specs.energyRating} onChange={(e) => handleSpecChange('energyRating', e.target.value)}>
                    <option value="">Select</option>
                    {[1,2,3,4,5].map(n => <option key={n} value={n}>{n} Star</option>)}
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tags and Features */}
        <div className="mb-3">
          <label className="form-label">Tags (comma / semicolon / newlines)</label>
          <input type="text" className="form-control" value={tagsText} onChange={(e) => setTagsText(e.target.value)} placeholder="flagship, gaming, camera" />
        </div>
        <div className="mb-3">
          <label className="form-label">Key Features (comma / semicolon / newlines)</label>
          <textarea className="form-control" rows="2" value={featuresText} onChange={(e) => setFeaturesText(e.target.value)} placeholder="Fast charging; Water resistant"></textarea>
        </div>

        {/* Mode Selection */}
        <div className="mb-4">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Product Creation Mode</h5>
            </div>
            <div className="card-body">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="creationMode"
                  id="singleMode"
                  checked={!bulkVariantMode}
                  onChange={() => setBulkVariantMode(false)}
                />
                <label className="form-check-label" htmlFor="singleMode">
                  <strong>Single Product</strong> - Add one product with optional variants
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="creationMode"
                  id="bulkMode"
                  checked={bulkVariantMode}
                  onChange={() => setBulkVariantMode(true)}
                />
                <label className="form-check-label" htmlFor="bulkMode">
                  <strong>Bulk Variants</strong> - Create multiple products with different RAM/Storage combinations
                </label>
              </div>
            </div>
          </div>
        </div>

        {!bulkVariantMode ? (
          /* Single Product Mode */
          <>
        <div className="row">
          <div className="col-md-4 mb-3">
            <label htmlFor="buyingPrice" className="form-label">Buying Price</label>
            <input
              type="number"
              className="form-control"
              id="buyingPrice"
              name="buyingPrice"
              value={product.buyingPrice}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="col-md-4 mb-3">
            <label htmlFor="price" className="form-label">Selling Price</label>
            <input
              type="number"
              className="form-control"
              id="price"
              name="price"
              value={product.price}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="col-md-4 mb-3">
            <label htmlFor="offerPrice" className="form-label">Offer Price (Optional)</label>
            <input
              type="number"
              className="form-control"
              id="offerPrice"
              name="offerPrice"
              value={product.offerPrice}
              onChange={handleChange}
            />
          </div>
        </div>
        
        <div className="row">
          <div className="col-md-6 mb-3">
            <label htmlFor="stock" className="form-label">Stock</label>
            <input
              type="number"
              className="form-control"
              id="stock"
              name="stock"
              value={product.stock}
              onChange={handleChange}
              required
            />
          </div>
        </div>
        
        {/* Images Section */}
        <div className="mb-4">
          <h5>Product Images</h5>
          <div className="mb-3">
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder="Enter image URL"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={handleAddImage}
              >
                Add Image
              </button>
            </div>
            <small className="text-muted">Please enter the URL of the product image</small>
          </div>
          
          {product.imageUrl && (
            <div className="row">
              <div className="col-md-4 mb-3">
                <div className="card">
                  <img src={product.imageUrl} className="card-img-top" alt="Product" style={{ height: '200px', objectFit: 'contain' }} />
                  <div className="card-body p-2 text-center">
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={removeImage}
                    >
                      Remove Image
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        </>
        ) : (
          /* Bulk Variants Mode */
          <>
            {/* Images Section for Bulk Mode */}
            <div className="mb-4">
              <h5>Product Images</h5>
              <div className="mb-3">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter image URL"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={handleAddImage}
                  >
                    Add Image
                  </button>
                </div>
                <small className="text-muted">This image will be used for all variants</small>
              </div>
              
              {product.imageUrl && (
                <div className="row">
                  <div className="col-md-4 mb-3">
                    <div className="card">
                      <img src={product.imageUrl} className="card-img-top" alt="Product" style={{ height: '200px', objectFit: 'contain' }} />
                      <div className="card-body p-2 text-center">
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={removeImage}
                        >
                          Remove Image
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Bulk Variant Configuration */}
            <div className="mb-4">
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">Bulk Variant Configuration</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Select RAM Options</label>
                      <div className="border rounded p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {ramOptions.map(ram => (
                          <div key={ram} className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`ram-${ram}`}
                              checked={selectedRams.includes(ram)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedRams([...selectedRams, ram]);
                                } else {
                                  setSelectedRams(selectedRams.filter(r => r !== ram));
                                }
                              }}
                            />
                            <label className="form-check-label" htmlFor={`ram-${ram}`}>
                              {ram}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Select Storage Options</label>
                      <div className="border rounded p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {storageOptions.map(storage => (
                          <div key={storage} className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`storage-${storage}`}
                              checked={selectedStorages.includes(storage)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedStorages([...selectedStorages, storage]);
                                } else {
                                  setSelectedStorages(selectedStorages.filter(s => s !== storage));
                                }
                              }}
                            />
                            <label className="form-check-label" htmlFor={`storage-${storage}`}>
                              {storage}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                    </div>
                    
                  <div className="text-center">
                      <button
                        type="button"
                      className="btn btn-primary"
                      onClick={generateBulkVariants}
                      >
                      Generate Variants
                      </button>
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Select RAM Options</label>
                      <div className="border rounded p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {ramOptions.map(ram => (
                          <div key={ram} className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`ram-${ram}`}
                              checked={selectedRams.includes(ram)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedRams([...selectedRams, ram]);
                                } else {
                                  setSelectedRams(selectedRams.filter(r => r !== ram));
                                }
                              }}
                            />
                            <label className="form-check-label" htmlFor={`ram-${ram}`}>
                              {ram}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Select Storage Options</label>
                      <div className="border rounded p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {storageOptions.map(storage => (
                          <div key={storage} className="form-check">
                      <input
                              className="form-check-input"
                              type="checkbox"
                              id={`storage-${storage}`}
                              checked={selectedStorages.includes(storage)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedStorages([...selectedStorages, storage]);
                                } else {
                                  setSelectedStorages(selectedStorages.filter(s => s !== storage));
                                }
                              }}
                            />
                            <label className="form-check-label" htmlFor={`storage-${storage}`}>
                              {storage}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
                    </div>
                    
            {/* Generated Variants Preview */}
            {bulkVariants.length > 0 && (
              <div className="mb-4">
                <div className="card">
                  <div className="card-header">
                    <h5 className="mb-0">Generated Variants ({bulkVariants.length} products)</h5>
                    <small className="text-muted">Fill in the prices and stock for each variant manually</small>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th>Product Name</th>
                            <th>Variant</th>
                            <th>Price (₹) *</th>
                            <th>Buying Price (₹) *</th>
                            <th>Stock *</th>
                            <th>Offer Price (₹)</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bulkVariants.map((variant, index) => (
                            <tr key={index}>
                              <td><strong>{product.name} {variant.value}</strong></td>
                              <td><span className="badge bg-primary">{variant.value}</span></td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={variant.price}
                                  onChange={(e) => updateBulkVariant(index, 'price', e.target.value)}
                                  placeholder="e.g., 15000"
                                  required
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={variant.buyingPrice}
                                  onChange={(e) => updateBulkVariant(index, 'buyingPrice', e.target.value)}
                                  placeholder="e.g., 12000"
                                  required
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={variant.stock}
                                  onChange={(e) => updateBulkVariant(index, 'stock', e.target.value)}
                                  placeholder="e.g., 5"
                                  required
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={variant.offerPrice}
                                  onChange={(e) => updateBulkVariant(index, 'offerPrice', e.target.value)}
                                  placeholder="Optional"
                                />
                              </td>
                              <td>
                      <button
                        type="button"
                                  className="btn btn-sm btn-danger"
                                  onClick={() => removeBulkVariant(index)}
                      >
                        Remove
                      </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-3">
                      <small className="text-muted">* Required fields</small>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        
        <div className="d-grid gap-2 d-md-flex justify-content-md-end">
          <button
            type="button"
            className="btn btn-secondary me-md-2"
            onClick={() => navigate('/')}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Adding...' : (bulkVariantMode ? `Add ${bulkVariants.length} Products` : 'Add Product')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AddProduct; 